import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ExternalLink, Loader2, LogIn } from "lucide-react";
import { listSubmissions } from "@/lib/board.functions";
import { formatIDR } from "@/lib/board-constants";
import { SiteFooter, SiteHeader } from "@/components/site-chrome";
import { useSession } from "@/lib/auth";
import { OWNER_PREFIX } from "@/lib/owner-store";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/riwayat")({
  head: () => ({
    meta: [
      { title: "Riwayat Pendaftaran — jasasebar.com" },
      {
        name: "description",
        content: "Pantau status setiap pendaftaran listing yang pernah Anda kirim ke papan peringkat jasasebar.com.",
      },
      { property: "og:title", content: "Riwayat Pendaftaran — jasasebar.com" },
      {
        property: "og:description",
        content: "Status, nilai bid, klik, dan catatan moderasi untuk setiap listing yang Anda daftarkan.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "/riwayat" }],
  }),
  component: RiwayatPage,
});

const STATUS_LABEL: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  approved: { label: "Tayang", variant: "default" },
  pending: { label: "Menunggu review", variant: "secondary" },
  rejected: { label: "Ditolak", variant: "destructive" },
};

function RiwayatPage() {
  const session = useSession();
  const listFn = useServerFn(listSubmissions);
  const [slugs, setSlugs] = useState<string[]>([]);

  useEffect(() => {
    const found: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(OWNER_PREFIX)) found.push(key.slice(OWNER_PREFIX.length));
    }
    setSlugs(found);
  }, []);

  const gated = session.enabled && !session.isAuthenticated;
  const email = session.email ?? undefined;
  const { data, isLoading } = useQuery({
    queryKey: ["submissions", email ?? "-", slugs.join(",")],
    queryFn: () => listFn({ data: { ...(email ? { email } : {}), slugs } }),
    enabled: !gated && (Boolean(email) || slugs.length > 0),
  });

  const items = data?.items ?? [];

  return (
    <div className="min-h-screen">
      <SiteHeader />
      <main className="mx-auto max-w-4xl px-4 py-10">
        <h1 className="font-display text-2xl font-semibold">Riwayat pendaftaran</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Semua listing yang pernah Anda kirim beserta status moderasi dan performanya.
          {session.enabled && !session.isAuthenticated
            ? " Masuk dengan Google agar riwayat tetap ada di semua perangkat."
            : ""}
        </p>

        {gated && (
          <div className="surface-card mt-6 p-6 text-center">
            <p className="text-sm text-muted-foreground">
              Halaman ini khusus pengguna yang sudah masuk. Silakan masuk dengan Google untuk melihat riwayat
              pendaftaran Anda.
            </p>
            <Button className="mt-4" onClick={session.login} size="sm" disabled={session.isLoading}>
              {session.isLoading ? <Loader2 className="size-4 animate-spin" /> : <LogIn className="size-4" />} Masuk
              dengan Google
            </Button>
          </div>
        )}

        {!gated && (
        <div className="mt-6 grid gap-2.5">
          {isLoading && (
            <>
              <Skeleton className="h-24 w-full rounded-xl" />
              <Skeleton className="h-24 w-full rounded-xl" />
            </>
          )}

          {!isLoading && items.length === 0 && (
            <div className="surface-card p-6 text-center text-sm text-muted-foreground">
              Belum ada pendaftaran.{" "}
              <Link to="/" hash="daftar" className="text-primary underline-offset-4 hover:underline">
                Daftarkan jasa Anda gratis
              </Link>
              .
            </div>
          )}

          {items.map((item) => {
            const st = STATUS_LABEL[item.status] ?? { label: item.status, variant: "outline" as const };
            return (
              <div key={item.slug} className="surface-card p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h2 className="truncate font-medium">{item.name}</h2>
                      <Badge variant={st.variant}>{st.label}</Badge>
                    </div>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">
                      {item.category} · dikirim {new Date(item.created_at).toLocaleDateString("id-ID")}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button asChild size="sm" variant="secondary">
                      <Link to="/produk/$slug" params={{ slug: item.slug }}>
                        Kelola
                      </Link>
                    </Button>
                    <Button asChild size="sm" variant="ghost">
                      <a href={item.url} target="_blank" rel="noopener noreferrer" aria-label="Buka tautan">
                        <ExternalLink className="size-4" />
                      </a>
                    </Button>
                  </div>
                </div>

                {item.admin_note && (
                  <p className="mt-2 rounded-lg bg-secondary px-3 py-2 text-xs text-muted-foreground">
                    Catatan admin: {item.admin_note}
                  </p>
                )}

                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Metric label="Total bid" value={formatIDR(item.total_amount)} />
                  <Metric label="Dibayar" value={formatIDR(item.paid_amount)} />
                  <Metric label="Klik" value={String(item.clicks)} />
                  <Metric label="Transaksi" value={String(item.orders)} />
                </div>
              </div>
            );
          })}
        </div>
        )}

        {!gated && isLoading && <Loader2 className="mx-auto mt-4 size-4 animate-spin text-muted-foreground" />}
      </main>
      <SiteFooter />
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm font-medium tabular-nums">{value}</p>
    </div>
  );
}
